import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/fun-ui/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/fun-ui/Badge"
import { YearPicker } from "@/components/fun-ui/YearPicker"
import { useFetchPaper, usePaper, usePaperMetadata } from "@/stores/appStore"
import Loading from "@/components/Loading"
import { PaperId, PaperMetadata } from "@/stores/slices/paperSlice"
import { DoiInput } from "./DoiInput"
import { AuthorsSelector } from "./AuthorsSelector"
import { cn } from "@/lib/utils"

const formItems = [
  {
    key: 'doi',
    label: 'DOI',
    component: 'doiInput',
    props: {
      placeholder: '请输入 DOI',
      maxLength: 200,
    }
  },
  {
    key: 'title',
    label: '标题',
    required: true,
    requiredMessage: '标题不能为空！',
    component: 'input',
    props: {
      placeholder: '请输入标题',
      maxLength: 800,
    }
  },
  {
    key: 'links',
    label: '',
    component: 'links',
    props: {

    }
  },
  {
    key: 'author',
    label: '作者',
    component: 'authorsSelector',
    props: {
      placeholder: '请添加作者',
    }
  },
  {
    key: 'url',
    label: '网址',
    component: 'input',
    props: {
      placeholder: '请输入网址',
      maxLength: 800,
    }
  },
  {
    key: 'organization',
    label: '机构',
    component: 'input',
    props: {
      placeholder: '请输入机构',
      maxLength: 1900,
    }
  },
  {
    key: 'school',
    label: '学校',
    component: 'input',
    props: {
      placeholder: '请输入学校',
      maxLength: 400,
    }
  },
  {
    key: 'journal',
    label: '期刊名称',
    component: 'input',
    props: {
      placeholder: '请输入期刊名称',
      maxLength: 400,
    }
  },
  {
    key: 'year',
    label: '出版年份',
    component: 'yearPicker',
    props: {
      placeholder: '请选择出版年份',
    }
  },
  {
    key: 'issue',
    label: '期号',
    component: 'input',
    props: {
      placeholder: '请输入期号',
      maxLength: 40,
    }
  },
  {
    key: 'volume',
    label: '卷次',
    component: 'input',
    props: {
      placeholder: '请输入卷次',
      maxLength: 40,
    }
  },
  {
    key: 'pages',
    label: '页码',
    component: 'input',
    props: {
      placeholder: '请输入页码',
      maxLength: 40,
    }
  },
  {
    key: 'type',
    label: '文献类型',
    component: 'select',
    props: {
      placeholder: '请选择文献类型',
      enum: [
        { "label": "预印本", "value": "article" },
        { "label": "期刊文章", "value": "article-journal" },
        { "label": "杂志文章", "value": "article-magazine" },
        { "label": "报纸文章", "value": "article-newspaper" },
        { "label": "法案", "value": "bill" },
        { "label": "图书", "value": "book" },
        { "label": "播客/电视广播/电台广播", "value": "broadcast" },
        { "label": "图书章节", "value": "chapter" },
        { "label": "数据集", "value": "dataset" },
        { "label": "文档/附件/笔记", "value": "document" },
        { "label": "词条", "value": "entry-dictionary" },
        { "label": "百科条目", "value": "entry-encyclopedia" },
        { "label": "艺术品", "value": "graphic" },
        { "label": "听证会", "value": "hearing" },
        { "label": "访谈", "value": "interview" },
        { "label": "司法案例", "value": "legal_case" },
        { "label": "法律", "value": "legislation" },
        { "label": "手稿", "value": "manuscript" },
        { "label": "地图", "value": "map" },
        { "label": "电影/视频", "value": "motion_picture" },
        { "label": "会议论文", "value": "paper-conference" },
        { "label": "专利", "value": "patent" },
        { "label": "信件/电子邮件/即时消息", "value": "personal_communication" },
        { "label": "论坛帖文", "value": "post" },
        { "label": "博客帖文", "value": "post-weblog" },
        { "label": "报告", "value": "report" },
        { "label": "软件", "value": "software" },
        { "label": "音频", "value": "song" },
        { "label": "演示文档", "value": "speech" },
        { "label": "标准", "value": "standard" },
        { "label": "学位论文", "value": "thesis" },
        { "label": "网页", "value": "webpage" }
      ]
    }
  }
];

const defaultValues: PaperMetadata = {
  doi: '',
  title: '',
  author: '',
  journal: '',
  volume: '',
  issue: '',
  year: '',
  pages: '',
  type: '',
  organization: '',
  school: '',
  url: '',
  citedByCount: 0,
}

export type ModifyPaperInfoButtonOptions = {
  paperId: PaperId;
  confirm?: () => void;
  asyncConfirm?: () => Promise<void>;
  children?: React.ReactNode;
  className?: string;
  withLoadingLabel?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function ModifyPaperInfoButton({
  paperId,
  confirm = () => { },
  asyncConfirm = async () => { },
  children,
  className = '',
  withLoadingLabel = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ModifyPaperInfoButtonOptions) {
  const paper = usePaper(paperId);
  const { fetchPaper, fetchLoading } = useFetchPaper();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = typeof controlledOpen !== 'undefined';
  const isOpen = isControlled ? controlledOpen : internalIsOpen;

  const handleOpenChange = async (newOpen: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(newOpen);
    }
    if (controlledOnOpenChange) {
      controlledOnOpenChange(newOpen);
    }
    if (newOpen === true) {
      // 载入新数据
      await fetchPaper(paperId);
    }
  };

  // Add effect to handle initial open state or prop changes for controlled mode
  useEffect(() => {
    if (isControlled && controlledOpen === true) {
      fetchPaper(paperId);
    }
  }, [controlledOpen, fetchPaper, isControlled, paperId]); // fetchPaper is stable (likely from store) but better add it to deps if not sure

  const [isSaving, setIsSaving] = useState(false);
  const {
    savePaperMetadata,
    currentPaperMetadataByDoi: metadata,
    clearCurrentPaperMetadataByDoi,
  } = usePaperMetadata();

  const [values, setValues] = useState<PaperMetadata>({
    ...defaultValues,
  });
  const valuesRef = useRef<PaperMetadata>({
    ...defaultValues,
  });
  const [isValuesModify, setIsValuesModify] = useState(
    {} as Record<keyof PaperMetadata, boolean>
  );
  const [links, setLinks] = useState<{
    text: string;
    url: string;
  }[]>([]);
  useEffect(() => {
    if (paper) {
      const { url } = paper;
      const {
        doi,
        title,
        author,
        journal,
        volume,
        issue,
        year,
        pages,
        type,
        organization,
        school,
        citedByCount,
      } = paper.extra;
      setValues({
        doi,
        title,
        author,
        journal,
        volume,
        issue,
        year,
        pages,
        type,
        organization,
        school,
        url,
        citedByCount,
      });
      setLinks(paper.links);
    }
  }, [paper]);

  const updateValue = useCallback((key: string, value: string) => {
    setValues({
      ...valuesRef.current,
      [key]: value,
    });
  }, [setValues]);

  useEffect(() => {
    valuesRef.current = {
      ...values,
    };
  }, [values]);

  useEffect(() => {
    if (!isOpen && metadata) {
      clearCurrentPaperMetadataByDoi();
      setValues({
        ...defaultValues
      });
    }
  }, [isOpen, metadata, clearCurrentPaperMetadataByDoi]);


  const {
    fetchPaperMetadataLoading,
  } = usePaperMetadata();


  const verificationPassed = useMemo(() => {
    return formItems.some((item) => (
      item.required && values[item.key as keyof PaperMetadata]
    ));
  }, [values]);

  useEffect(() => {
    if (metadata) {
      const values = valuesRef.current;
      const newValues = Object.keys(values).reduce((acc, key) => {
        return {
          ...acc,
          [key]: metadata[key as keyof PaperMetadata] || '',
        }
      }, {} as PaperMetadata) as PaperMetadata;
      const isValuesModify = Object.keys(values).reduce((acc, _key) => {
        const key = _key as keyof PaperMetadata;
        return {
          ...acc,
          [key]: values[key] !== newValues[key],
        }
      }, {} as Record<keyof PaperMetadata, boolean>);
      setIsValuesModify(isValuesModify);
      setValues(newValues);
    }
  }, [metadata]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild>
        {children || <Button
          variant="link"
          className={cn(
            "!no-underline !text-sm !text-primary hover:!font-medium h-auto font-normal",
            className,
          )}
        >
          修正信息
        </Button>}
      </DialogTrigger>
      <DialogContent tabIndex={-1} className="flex flex-col max-h-[80%] overflow-hidden px-0 max-w-2xl">
        <DialogHeader className="px-8">
          <DialogTitle>题录信息</DialogTitle>
        </DialogHeader>
        <div className="flex-1 px-8 flex flex-col overflow-auto pb-4">
          {
            formItems.map(({
              key: _key,
              label,
              required = false,
              requiredMessage = '',
              component,
              props,
            }) => {
              const key = _key as keyof PaperMetadata;
              if (component === 'links') {
                return (
                  <div key={key} className="text-xs flex text-muted-foreground space-x-[10px]  mt-3">
                    {
                      links.map(({ text, url }, index) => {
                        return (
                          <React.Fragment key={text}>
                            {index > 0 ? <div className="bg-border h-3 w-[1px]" /> : null}
                            <a className="hover:text-primary" target="_blank" href={url}>{text}</a>
                          </React.Fragment>
                        )
                      })
                    }
                  </div>
                );
              }
              if (component === 'doiInput') {
                return (
                  <div
                    key={key}
                    className="flex flex-col"
                    data-value={values[key]}
                  >
                    <Label className="flex h-5 items-center">
                      <span>{label}</span>
                      {
                        required ? (
                          <span className="text-destructive ml-2">*</span>
                        ) : null
                      }
                    </Label>
                    <div className="relative mt-3">
                      <DoiInput
                        value={String(values[key] ?? '')}
                        onChange={(e) => {
                          updateValue(key, e.currentTarget.value);
                        }}
                        placeholder={props.placeholder || ''}
                        maxLength={props.maxLength || 1000}
                      />
                    </div>
                  </div>
                );
              }
              if (component === 'authorsSelector') {
                return (
                  <div
                    key={key}
                    className="flex flex-col  mt-9"
                    data-value={values[key]}
                  >
                    <Label className="flex h-5 items-center">
                      <span>{label}</span>
                      {
                        required ? (
                          <span className="text-destructive ml-2">*</span>
                        ) : null
                      }
                      {
                        metadata && isValuesModify[key] ? (
                          <Badge variant="primary-outline" className="ml-4">有更新</Badge>
                        ) : null
                      }
                    </Label>
                    <div className="relative mt-3">
                      <AuthorsSelector
                        disabled={fetchPaperMetadataLoading}
                        value={String(values[key] ?? '')}
                        onValueChange={(value) => {
                          updateValue(key, value);
                        }}
                        maxLength={100}
                        maxCounts={20}
                        placeholder={props.placeholder || ''}
                      />
                    </div>
                    {
                      !fetchLoading && !fetchPaperMetadataLoading && required && !values[key] ? (
                        <div className="text-destructive text-xs mt-1">{requiredMessage}</div>
                      ) : null
                    }
                    {
                      !fetchLoading && !fetchPaperMetadataLoading && props.maxLength && String(values[key] ?? '').length > props.maxLength ? (
                        <div className="text-destructive text-xs mt-1">{`最大支持${props.maxLength}字`}</div>
                      ) : null
                    }
                  </div>
                );
              }
              if (component === 'input') {
                return (
                  <div
                    key={key}
                    className="flex flex-col  mt-9"
                    data-value={values[key]}
                  >
                    <Label className="flex h-5 items-center">
                      <span>{label}</span>
                      {
                        required ? (
                          <span className="text-destructive ml-2">*</span>
                        ) : null
                      }
                      {
                        metadata && isValuesModify[key] ? (
                          <Badge variant="primary-outline" className="ml-4">有更新</Badge>
                        ) : null
                      }
                    </Label>
                    <div className="relative mt-3">
                      <Input
                        disabled={fetchPaperMetadataLoading}
                        value={String(values[key] ?? '')}
                        onChange={(e) => {
                          updateValue(key, e.currentTarget.value);
                        }}
                        placeholder={props.placeholder || ''}
                        maxLength={props.maxLength || 1000}
                      />
                    </div>
                    {
                      !fetchLoading && !fetchPaperMetadataLoading && required && !values[key] ? (
                        <div className="text-destructive text-xs mt-1">{requiredMessage}</div>
                      ) : null
                    }
                    {
                      !fetchLoading && !fetchPaperMetadataLoading && props.maxLength && String(values[key] ?? '').length > props.maxLength ? (
                        <div className="text-destructive text-xs mt-1">{`最大支持${props.maxLength}字`}</div>
                      ) : null
                    }
                  </div>
                );
              }
              if (component === 'select') {
                return (
                  <div
                    key={key}
                    className="flex flex-col  mt-9"
                    data-value={values[key]}
                  >
                    <Label className="flex h-5 items-center">
                      <span>{label}</span>
                      {
                        required ? (
                          <span className="text-destructive ml-2">*</span>
                        ) : null
                      }
                      {
                        metadata && isValuesModify[key] ? (
                          <Badge variant="primary-outline" className="ml-4">有更新</Badge>
                        ) : null
                      }
                    </Label>
                    <Select
                      value={String(values[key] ?? '')}
                      disabled={fetchPaperMetadataLoading}
                      onValueChange={(value) => {
                        updateValue(key, value);
                      }}
                    >
                      <SelectTrigger
                        data-value={values[key]}
                        className="mt-3 h-10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <SelectValue
                          placeholder={
                            <span className="text-annotation-foreground"
                            >{props.placeholder}</span>
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {
                            props.enum!.map(({ label, value }) => {
                              return (
                                <SelectItem
                                  key={value}
                                  value={value}
                                  className="cursor-pointer hover:bg-accent"
                                >{label}</SelectItem>
                              )
                            })
                          }
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              if (component === 'yearPicker') {
                return (
                  <div
                    key={key}
                    className="flex flex-col  mt-9"
                    data-value={values[key]}
                  >
                    <Label className="flex h-5 items-center">
                      <span>{label}</span>
                      {
                        required ? (
                          <span className="text-destructive ml-2">*</span>
                        ) : null
                      }
                      {
                        metadata && isValuesModify[key] ? (
                          <Badge variant="primary-outline" className="ml-4">有更新</Badge>
                        ) : null
                      }
                    </Label>
                    <div className="relative mt-3">
                      <YearPicker
                        disabled={fetchPaperMetadataLoading}
                        value={Number(values[key])}
                        onValueChange={(value) => {
                          updateValue(key, value.toString());
                        }}
                        placeholder={props.placeholder}
                      />
                    </div>
                  </div>
                )
              }
            })
          }
        </div>
        <DialogFooter className="flex space-x-4 px-8">
          <Button
            variant="outline"
            onClick={() => {
              handleOpenChange(false);
            }}
          >取消</Button>
          <Button
            disabled={isSaving || fetchPaperMetadataLoading || !verificationPassed}
            onClick={async () => {
              setIsSaving(true);
              const success = await savePaperMetadata({
                paperId,
                metadata: values,
              });
              if (success) {
                confirm();
                await asyncConfirm();
                handleOpenChange(false);
              }
              setIsSaving(false);
            }}
          >确认</Button>
        </DialogFooter>
        {
          fetchLoading ? (
            <div>
              <Loading mask >
                {withLoadingLabel ? <span className="text-lg ml-2">AI 正在提取数据</span> : null}
              </Loading>
            </div>
          ) : null
        }
        <div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

